import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../config/api';
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Lock, Shield, AlertTriangle } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import '../styles/auth.css'

const ChangePassword = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuth()
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Verificar autenticación y redirigir si no está autenticado
  useEffect(() => {
    if (!isAuthenticated()) {
      toast.error('Debes iniciar sesión para cambiar tu contraseña')
      navigate('/login', { replace: true })
    }
  }, [navigate, isAuthenticated])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validaciones
    if (formData.newPassword.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres')
      return
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden')
      return
    }

    if (formData.currentPassword === formData.newPassword) {
      toast.error('La nueva contraseña debe ser diferente a la contraseña temporal')
      return
    }

    setIsSubmitting(true)

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      
      const response = await fetch(`${API_BASE_URL}/api/auth/change-temp-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Error al cambiar la contraseña')
      }

      if (data.success) {
        toast.success('Contraseña cambiada exitosamente')
        // Redirigir al dashboard después de 1 segundo
        setTimeout(() => {
          navigate('/dashboard', { replace: true })
        }, 1000)
      } else {
        throw new Error(data.message || 'Error al cambiar la contraseña')
      }
    } catch (error) {
      console.error('Error:', error)
      toast.error(error.message || 'Error al cambiar la contraseña')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isAuthenticated()) {
    return null // No mostrar nada mientras redirige
  }

  return (
    <div className="auth-body">
      <div className="auth-container">
        <div className="auth-card">
          {/* Header */}
          <div className="auth-header">
            <div className="logo">
              <Shield size={48} />
              <h1>Cambiar Contraseña</h1>
            </div>
            <p className="auth-subtitle">Debes cambiar tu contraseña temporal para continuar</p>
          </div>

          {/* Alerta */}
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 193, 7, 0.05) 100%)',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '2px solid rgba(255, 193, 7, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <AlertTriangle size={20} color="#f59e0b" />
            <div>
              <strong style={{ color: '#f59e0b', display: 'block', marginBottom: '0.25rem' }}>
                ⚠️ Contraseña Temporal
              </strong>
              <span style={{ color: '#666', fontSize: '0.875rem' }}>
                Tu contraseña temporal ha expirado o debe ser cambiada por seguridad
              </span>
            </div>
          </div>

          {/* Requisitos de contraseña */}
          <div style={{
            background: '#f8f9fa',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            textAlign: 'left'
          }}>
            <h4 style={{ color: '#C8202F', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
              Requisitos para la nueva contraseña:
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ color: '#666', fontSize: '0.85rem', marginBottom: '0.25rem', paddingLeft: '1.5rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: '#28a745', fontWeight: 'bold' }}>✓</span>
                Mínimo 6 caracteres
              </li>
              <li style={{ color: '#666', fontSize: '0.85rem', marginBottom: '0.25rem', paddingLeft: '1.5rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: '#28a745', fontWeight: 'bold' }}>✓</span>
                No puede ser la misma que la contraseña temporal
              </li>
              <li style={{ color: '#666', fontSize: '0.85rem', marginBottom: '0.25rem', paddingLeft: '1.5rem', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 0, color: '#28a745', fontWeight: 'bold' }}>✓</span>
                Se recomienda usar letras, números y símbolos
              </li>
            </ul>
          </div>

          {/* Formulario */}
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="currentPassword">Contraseña Actual (Temporal):</label>
              <div className="input-group">
                <Lock size={20} />
                <input
                  id="currentPassword"
                  name="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  required
                  value={formData.currentPassword}
                  onChange={handleChange}
                  placeholder="Ingresa tu contraseña temporal"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">Nueva Contraseña:</label>
              <div className="input-group">
                <Lock size={20} />
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  required
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Ingresa tu nueva contraseña"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar Nueva Contraseña:</label>
              <div className="input-group">
                <Lock size={20} />
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirma tu nueva contraseña"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="auth-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Cambiando contraseña...
                </>
              ) : (
                <>
                  <Shield size={18} />
                  Cambiar Contraseña
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="auth-footer">
            <p>© 2025 INDUMA S.A.S - Todos los derechos reservados | create by "jpz"</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChangePassword

